import { FC, Fragment, useState } from "react"
import ReactSpeedometer from "react-d3-speedometer"
import { formatRequestData } from "../utils/dataFormat"
import { Transition } from "@headlessui/react"
import { useMediaQuery } from "react-responsive"
// import ApiClient from "../api/ApiClient"
// import { logEvent } from "../utils/logEvent"
import { WasmRunner } from "@coherentglobal/wasm-runner"

export type PerformanceFormProps = {
  /**
   * Calculated response data to display to the user
   */
  runner: WasmRunner
}

export const PerformanceForm: FC<PerformanceFormProps> = ({ runner }) => {
  const [modelRan, setModelRan] = useState<boolean>(false)
  const [avgRecalc, setAvgRecalc] = useState<number>(0)
  const [speedometerMaxValue] = useState<number>(150)
  const [avgSpeedometerRecalc, setAvgSpeedometerRecalc] = useState<number>(0)
  const [calculating, setCalculating] = useState<boolean>(false)

  const runCalculations = async () => {
    const times = []
    for (let i = 0; i < 100; i++) {
      const response = await runner.execute(
        formatRequestData({
          Active_Ppts: 100,
          Deposit_Annual: 125000,
          Eligible_EEs: 100,
          Fee_Asset: 0.0005,
          Fee_Deposit: 0.002,
          Fund_Type: "Mid Market",
          Inactive_Ppts: 500,
          Product: "Product Basic",
          RolloverContributions: 100000,
          ServiceModel: "Service Basic",
          SV2_Fund: "Cash",
          TDF_Fund_Name: "TDF Fund D",
          TotalAssets: 6500000,
        })
      )
      let processTime = response?.response_meta?.process_time
      times.push(processTime)
      setAvgRecalc(processTime)
      setAvgSpeedometerRecalc(speedometerMaxValue - Math.abs(speedometerMaxValue - processTime))
    }
    const average = times.reduce((a, b) => a + b) / times.length
    setCalculating(false)
    setAvgRecalc(average)
    setAvgSpeedometerRecalc(speedometerMaxValue - Math.abs(speedometerMaxValue - average))
  }

  const reRunCalculations = () =>
    setTimeout(() => {
      runCalculations()
    }, 500)

  const isMobile = useMediaQuery({ query: "(max-width: 1024px)" })

  return (
    <div className="w-full font-semibold">
      <div className="h-24 w-full flex items-center justify-center">
        <h1 className="w-full max-w-xs lg:max-w-xl text-center text-black text-3xl">
          {modelRan && !calculating ? "Done!" : "Performance test."}
        </h1>
      </div>
      <div className="h-16 w-full flex items-center justify-center">
        <h1 className="w-full max-w-sm lg:max-w-full space-x-2 text-center text-black text-2xl">
          {modelRan ? (
            calculating ? (
              "Calculating..."
            ) : (
              <>
                Simulated 100 scenarios on your device.&nbsp;
                {!isMobile && <br />}
                {avgRecalc}ms per recalc.
              </>
            )
          ) : (
            "Run 100 unique model points on your device."
          )}
        </h1>
      </div>
      <div className="w-full mx-auto mt-8 max-w-xs lg:max-w-sm flex flex-col items-center">
        {modelRan ? (
          <>
            <ReactSpeedometer
              needleHeightRatio={0.7}
              maxSegmentLabels={1}
              segments={1000}
              minValue={0}
              maxValue={speedometerMaxValue}
              ringWidth={10}
              needleColor="black"
              startColor="black"
              endColor="#972cf8"
              value={avgSpeedometerRecalc}
              currentValueText={String(avgRecalc) + "ms"}
              textColor="black"
              height={200}
              labelFontSize={"0"}
            />

            <Transition.Root show={!calculating} as={Fragment}>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="w-full space-y-1 lg:space-y-2 text-sm font-semibold pb-8">
                  <div className="flex justify-between">
                    <span>
                      MS Excel on desktop {isMobile && <br />}
                      (i5 / 4 cores)
                    </span>
                    <span className="text-brand-light w-14">42.7ms</span>
                  </div>
                  <div className="flex justify-between">
                    <span>
                      Spark WASM on iPad Air 2 {isMobile && <br />}
                      (A8x, 1 core used)
                    </span>
                    <span className="text-brand-light w-14">21.2ms</span>
                  </div>
                  <div className="flex justify-between">
                    <span>
                      Spark WASM on Samsung S22+ {isMobile && <br />}
                      (SD8, 1 core used)
                    </span>
                    <span className="text-brand-light w-14">7.8ms</span>
                  </div>
                  <div className="flex justify-between">
                    <span>
                      Spark WASM on Azure Cloud {isMobile && <br />}
                      (Xenon, 1 vCore)
                    </span>
                    <span className="text-brand-light w-14">6.7ms</span>
                  </div>
                  <div className="flex justify-between">
                    <span>
                      Spark WASM on iPhone 10xr {isMobile && <br />}
                      (A12, 1 core used)
                    </span>
                    <span className="text-brand-light w-14">5.7ms</span>
                  </div>
                  <div className="flex justify-between">
                    <span>
                      Spark WASM on AWS cloud {isMobile && <br />}
                      (Gen2 Graviton, 1 core)
                    </span>
                    <span className="text-brand-light w-14">5.6ms</span>
                  </div>
                  <div className="flex justify-between">
                    <span>
                      Spark WASM on iPhone 13 {isMobile && <br />}
                      (A15, 1 core used)
                    </span>
                    <span className="text-brand-light w-14">3.8ms</span>
                  </div>
                  <div className="flex justify-between">
                    <span>
                      Spark WASM on your device {isMobile && <br />}
                      (1 core used)
                    </span>
                    <span className="text-brand-light w-14">{String(avgRecalc) + "ms"}</span>
                  </div>

                  <div className="flex items-center justify-center w-full  h-20">
                    <button
                      className="bg-brand-light rounded text-white py-2 px-8 text-sm w-[50%]"
                      type="button"
                      onClick={() => {
                        // logEvent("DemoAppPerformanceTest")
                        setAvgRecalc(0)
                        setAvgSpeedometerRecalc(0)
                        setModelRan(true)
                        setCalculating(true)
                        reRunCalculations()
                      }}
                    >
                      RUN AGAIN
                    </button>
                  </div>
                </div>
              </Transition.Child>
            </Transition.Root>
          </>
        ) : (
          <button
            className="bg-brand-light text-white rounded text-8xl py-2 px-6"
            type="button"
            onClick={() => {
              // logEvent("DemoAppPerformanceTest")
              setModelRan(true)
              setCalculating(true)
              runCalculations()
            }}
          >
            GO!
          </button>
        )}
      </div>
      <div className="flex flex-col items-center justify-center space-y-3"></div>
    </div>
  )
}

